(() => {
  if (customElements.get('rf-kundenportal')) return;
  const safePath = (path) => typeof path === 'string' && /^\/(?:auftraege(?:\/[A-Za-z0-9_-]+)?|konto|login)$/.test(path);

  // Höchstens drei automatische Starts in zwei Minuten je Art — Schutz gegen Umleitungsschleifen.
  const allowAttempt = (key) => {
    let attempts = [];
    try {
      attempts = JSON.parse(sessionStorage.getItem(key) || '[]');
      if (!Array.isArray(attempts)) attempts = [];
    } catch (_) { attempts = []; }
    const now = Date.now();
    attempts = attempts.filter((t) => typeof t === 'number' && now - t < 120000);
    if (attempts.length >= 3) return false;
    attempts.push(now);
    try { sessionStorage.setItem(key, JSON.stringify(attempts)); } catch (_) { /* ohne Speicher: einmal pro Seitenaufruf */ }
    return true;
  };

  customElements.define('rf-kundenportal', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.frame = this.querySelector('[data-portal-frame]');
      this.status = this.querySelector('[data-portal-status]');
      this.notice = this.querySelector('[data-portal-notice]');
      let portal;
      try {
        portal = new URL(this.dataset.portalUrl);
        const localPreview = location.hostname === 'localhost' && portal.hostname === 'localhost' && portal.protocol === 'http:';
        if ((!localPreview && portal.protocol !== 'https:') || portal.username || portal.password) throw new Error('Invalid portal URL');
      } catch {
        this.status.textContent = 'Das Kundenportal ist gerade nicht verfügbar.';
        this.notice.hidden = false;
        this.frame.hidden = true;
        return;
      }
      this.portalOrigin = portal.origin;
      const current = new URL(location.href);
      const requested = current.searchParams.get('portal_path');
      // #1864: Rücksprung aus der Shopify-Anmeldung — Merker einmal lesen und aus der Adresse nehmen.
      this.returnedFromLogin = current.searchParams.get('rf_login') === '1';
      // Fehler der Anmeldung (nur bekannte Werte) an das Formular im Portal durchreichen.
      const fehler = current.searchParams.get('portal_fehler');
      if (this.returnedFromLogin || current.searchParams.has('portal_fehler')) {
        current.searchParams.delete('rf_login');
        current.searchParams.delete('portal_fehler');
        history.replaceState(history.state, '', current);
      }
      this.path = safePath(requested) ? requested : '/auftraege';

      // #1864: Zuerst im SHOP anmelden (eigene Anmeldung des Onlineshops) — nur diese Sitzung
      // kennen Shop und Checkout. Danach meldet sich das Portal über dieselbe Shopify-Sitzung
      // ohne erneuten Code an. Nicht im Theme-Editor; nicht nach einem Anmeldefehler.
      this.shopEmail = this.dataset.shopEmail || '';
      const designMode = window.Shopify && window.Shopify.designMode;
      if (!this.dataset.shopCustomer && !designMode && !fehler && allowAttempt('rf-shop-login')) {
        const back = new URL(location.pathname, location.origin);
        back.searchParams.set('portal_path', this.path);
        const login = new URL('/customer_authentication/login', location.origin);
        login.searchParams.set('return_to', back.pathname + back.search);
        this.status.textContent = 'Weiterleitung zur Anmeldung …';
        this.status.hidden = false;
        this.notice.hidden = false;
        this.frame.hidden = true;
        location.replace(login.href);
        return;
      }
      const frameUrl = new URL(this.path, this.portalOrigin);
      frameUrl.searchParams.set('embed', 'shopify');
      frameUrl.searchParams.set('parent_origin', location.origin);
      if (this.path === '/login' && (fehler === 'shopify' || fehler === 'link')) frameUrl.searchParams.set('fehler', fehler);

      const sendViewport = () => {
        if (this.animationFrame) return;
        this.animationFrame = requestAnimationFrame(() => {
          this.animationFrame = null;
          const rect = this.frame.getBoundingClientRect();
          const height = window.innerHeight;
          this.post({ type: 'rf-shop:viewport', top: Math.max(0, -rect.top), bottom: Math.max(0, Math.min(rect.height, height - rect.top)), height });
        });
      };
      const init = () => {
        // E-Mail des im Shop angemeldeten Kunden: Das Portal meldet eine fremde Sitzung ab
        // (geteilter Rechner). Nur Abgleich, keine Anmeldung — die läuft über Shopify.
        this.post({ type: 'rf-shop:init', email: this.shopEmail });
        sendViewport();
      };
      window.addEventListener('message', (event) => {
        if (event.origin !== this.portalOrigin || event.source !== this.frame.contentWindow) return;
        const data = event.data;
        if (!data || typeof data !== 'object') return;
        if (data.type === 'rf-portal:ready') {
          clearTimeout(this.timeout);
          this.status.hidden = true;
          this.notice.hidden = true;
          init();
        } else if (data.type === 'rf-portal:resize' && typeof data.height === 'number' && Number.isFinite(data.height)) {
          this.frame.style.height = `${Math.max(480, Math.min(30000, Math.ceil(data.height)))}px`;
          sendViewport();
        } else if (data.type === 'rf-portal:login' && safePath(data.path)) {
          // #1864: Nicht angemeldet ⇒ Shopify-Anmeldung im ganzen Fenster starten (das Portal im
          // iframe darf das Fenster nicht selbst umleiten). Danach führt das Portal hierher zurück
          // (?portal_path=…&rf_login=1). Schleifenschutz: Kommt man mit rf_login=1 zurück und ist
          // trotzdem nicht angemeldet, bleibt das Formular stehen; zusätzlich höchstens drei
          // Starts in zwei Minuten. Abbruch bei Shopify + erneuter Klick aufs Konto-Icon startet
          // dagegen wieder. Nie im Theme-Editor.
          if (window.Shopify && window.Shopify.designMode) return;
          if (this.autoLoginStarted || this.returnedFromLogin || !allowAttempt('rf-portal-auto-login')) return;
          this.autoLoginStarted = true;
          this.frame.style.visibility = 'hidden';
          this.status.textContent = 'Weiterleitung zur Anmeldung …';
          this.status.hidden = false;
          this.notice.hidden = false;
          const start = new URL('/auth/shopify', this.portalOrigin);
          start.searchParams.set('next', data.path);
          start.searchParams.set('ziel', 'shop');
          // replace statt assign: Zurück von Shopify führt zur Seite vor „Mein Konto“, nicht in
          // eine erneute Weiterleitung.
          location.replace(start.href);
        } else if (data.type === 'rf-portal:logout') {
          // Abmelden im Portal beendet auch die Shop-Sitzung (sonst wäre man beim nächsten
          // Klick aufs Konto-Icon ohne Code wieder drin).
          const logout = this.dataset.logoutUrl;
          if (this.shopEmail && logout && logout.startsWith('/')) location.assign(logout);
        } else if (data.type === 'rf-portal:navigate' && safePath(data.path)) {
          const changed = this.path !== data.path;
          this.path = data.path;
          const url = new URL(location.href);
          url.searchParams.set('portal_path', data.path);
          history.replaceState(history.state, '', url);
          if (changed) this.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      }, { signal });
      this.frame.addEventListener('load', init, { signal });
      window.addEventListener('scroll', sendViewport, { signal, passive: true });
      window.addEventListener('resize', sendViewport, { signal, passive: true });
      this.timeout = setTimeout(() => {
        this.status.hidden = false;
        this.notice.hidden = false;
        this.status.textContent = 'Das Laden dauert länger als gewohnt. Bitte lade die Seite neu.';
      }, 15000);
      this.frame.src = frameUrl.href;
    }

    post(message) {
      this.frame.contentWindow?.postMessage(message, this.portalOrigin);
    }

    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.timeout);
      cancelAnimationFrame(this.animationFrame);
    }
  });
})();
